/**
 * Audience Manager tab — custom and lookalike audiences built from WhatsApp data.
 */

import { useEffect, useState, useCallback } from 'react'
import { ChannelBadge } from '@/components/marketing/ChannelBadge'
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
  Button,
  Input,
  Badge,
  Spinner,
} from '@/components/ui'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui'
import {
  Plus,
  RefreshCw,
  Trash2,
  Users,
  Copy,
} from 'lucide-react'
import { useMarketing } from '@/hooks/useMarketing'
import { useToast } from '@/components/ui/Toast'
import type { Audience } from '@/types/marketing'

const SOURCE_TYPES = [
  { value: 'whatsapp_converters', label: 'WhatsApp Converters' },
  { value: 'whatsapp_leads', label: 'WhatsApp Leads' },
  { value: 'purchasers', label: 'Purchasers' },
  { value: 'manual', label: 'Manual' },
]

export function AudienceManager() {
  const {
    fetchStatus,
    syncAudiences,
    fetchAudiences,
    createCustomAudience,
    createLookalikeAudience,
    syncAudience,
    deleteAudience,
    hasAccount,
    audiences,
  } = useMarketing()
  const { addToast } = useToast()

  const [loading, setLoading] = useState(true)
  const [syncing, setSyncing] = useState(false)
  const [creating, setCreating] = useState(false)
  const [actionId, setActionId] = useState<string | null>(null)

  // Create form state
  const [showCreate, setShowCreate] = useState<'custom' | 'lookalike' | null>(null)
  const [audienceName, setAudienceName] = useState('')
  const [sourceType, setSourceType] = useState('whatsapp_converters')

  // Lookalike form
  const [seedAudienceId, setSeedAudienceId] = useState('')
  const [country, setCountry] = useState('US')
  const [ratio, setRatio] = useState('100')

  const loadAudiences = useCallback(async () => {
    setLoading(true)
    try {
      const status = await fetchStatus()
      if (status.hasAccount) {
        await fetchAudiences()
      }
    } catch {
      addToast({ title: 'Error', description: 'Failed to load audiences', type: 'error' })
    } finally {
      setLoading(false)
    }
  }, [fetchStatus, fetchAudiences, addToast])

  useEffect(() => {
    loadAudiences()
  }, [loadAudiences])

  const handleCreateCustom = async () => {
    if (!audienceName.trim()) return
    setCreating(true)
    try {
      await createCustomAudience({
        name: audienceName,
        sourceType,
      })
      addToast({ title: 'Created', description: `Audience "${audienceName}" created`, type: 'success' })
      setShowCreate(null)
      setAudienceName('')
    } catch (err) {
      addToast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', type: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const handleCreateLookalike = async () => {
    if (!audienceName.trim() || !seedAudienceId) return
    setCreating(true)
    try {
      await createLookalikeAudience({
        name: audienceName,
        seedAudienceId,
        country,
        ratio: parseInt(ratio),
      })
      addToast({ title: 'Created', description: `Lookalike audience created`, type: 'success' })
      setShowCreate(null)
      setAudienceName('')
    } catch (err) {
      addToast({ title: 'Error', description: err instanceof Error ? err.message : 'Failed', type: 'error' })
    } finally {
      setCreating(false)
    }
  }

  const handleSync = async (audience: Audience) => {
    setActionId(audience.id)
    try {
      const count = await syncAudience(audience.id)
      addToast({ title: 'Synced', description: `${count} contacts synced`, type: 'success' })
      await loadAudiences()
    } catch {
      addToast({ title: 'Error', description: 'Sync failed', type: 'error' })
    } finally {
      setActionId(null)
    }
  }

  const handleDelete = async (audience: Audience) => {
    setActionId(audience.id)
    try {
      await deleteAudience(audience.id)
      addToast({ title: 'Deleted', description: `"${audience.name}" removed`, type: 'success' })
    } catch {
      addToast({ title: 'Error', description: 'Delete failed', type: 'error' })
    } finally {
      setActionId(null)
    }
  }

  const customAudiences = audiences.filter((a) => a.audienceType === 'custom')

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner size="lg" />
      </div>
    )
  }

  if (!hasAccount) {
    return (
      <Card>
        <CardContent className="py-12 text-center text-muted-foreground">
          No Meta Ad Account connected. Go to <strong>Settings</strong> to connect.
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{audiences.length} audiences</p>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={async () => {
            setSyncing(true)
            try {
              const result = await syncAudiences()
              await fetchAudiences()
              addToast({ title: 'Synced', description: `${result.created} audiences imported`, type: 'success' })
            } catch {
              addToast({ title: 'Error', description: 'Failed to sync audiences from Meta', type: 'error' })
            } finally {
              setSyncing(false)
            }
          }} disabled={syncing}>
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
            {syncing ? 'Syncing...' : 'Sync from Meta'}
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowCreate('custom')}>
            <Plus className="w-4 h-4 mr-2" />
            Custom Audience
          </Button>
          <Button variant="secondary" size="sm" onClick={() => setShowCreate('lookalike')} disabled={customAudiences.length === 0}>
            <Copy className="w-4 h-4 mr-2" />
            Lookalike
          </Button>
        </div>
      </div>

      {/* Create Custom Audience Form */}
      {showCreate === 'custom' && (
        <Card>
          <CardHeader>
            <CardTitle>Create Custom Audience</CardTitle>
            <CardDescription>Build an audience from WhatsApp conversation data</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input
                value={audienceName}
                onChange={(e) => setAudienceName(e.target.value)}
                placeholder="e.g. CTWA Converters Q1"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Source</label>
              <Select value={sourceType} onValueChange={setSourceType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {SOURCE_TYPES.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreateCustom} disabled={creating || !audienceName.trim()}>
                {creating && <Spinner size="sm" className="mr-2" />}
                Create
              </Button>
              <Button variant="ghost" onClick={() => setShowCreate(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create Lookalike Audience Form */}
      {showCreate === 'lookalike' && (
        <Card>
          <CardHeader>
            <CardTitle>Create Lookalike Audience</CardTitle>
            <CardDescription>Find new people similar to your best customers</CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            <div>
              <label className="block text-sm font-medium mb-1">Name</label>
              <Input
                value={audienceName}
                onChange={(e) => setAudienceName(e.target.value)}
                placeholder="e.g. Lookalike - Converters US 1%"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">Seed Audience</label>
              <Select value={seedAudienceId} onValueChange={setSeedAudienceId}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a custom audience" />
                </SelectTrigger>
                <SelectContent>
                  {customAudiences.map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium mb-1">Country</label>
                <Input value={country} onChange={(e) => setCountry(e.target.value.toUpperCase())} maxLength={2} />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Ratio (%)</label>
                <Select value={ratio} onValueChange={setRatio}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="100">1%</SelectItem>
                    <SelectItem value="200">2%</SelectItem>
                    <SelectItem value="300">3%</SelectItem>
                    <SelectItem value="500">5%</SelectItem>
                    <SelectItem value="1000">10%</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <Button onClick={handleCreateLookalike} disabled={creating || !audienceName.trim() || !seedAudienceId}>
                {creating && <Spinner size="sm" className="mr-2" />}
                Create
              </Button>
              <Button variant="ghost" onClick={() => setShowCreate(null)}>Cancel</Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Audience List */}
      {audiences.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            No audiences yet. Create a custom audience from your WhatsApp data.
          </CardContent>
        </Card>
      ) : (
        <div className="max-h-[65vh] overflow-y-auto space-y-2 pr-1">
          {audiences.map((audience) => (
            <Card key={audience.id}>
              <CardContent className="py-3 px-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <div>
                    <p className="font-medium text-sm flex items-center gap-1.5">
                      <ChannelBadge channel={audience.channel} showLabel={false} />
                      {audience.name}
                    </p>
                    <p className="text-xs text-muted-foreground">
                      {audience.audienceType === 'custom' ? audience.sourceType?.replace(/_/g, ' ') : 'Lookalike'}
                      {audience.approximateCount != null && ` · ~${audience.approximateCount.toLocaleString()} users`}
                      {audience.lookalikeCountry && ` · ${audience.lookalikeCountry} ${(audience.lookalikeRatio || 0) / 100}%`}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge
                    variant={audience.status === 'ready' ? 'success' : audience.status === 'error' ? 'destructive' : 'secondary'}
                  >
                    {audience.status}
                  </Badge>
                  {audience.audienceType === 'custom' && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSync(audience)}
                      disabled={actionId === audience.id}
                      title="Sync contacts"
                    >
                      {actionId === audience.id ? (
                        <Spinner size="sm" />
                      ) : (
                        <RefreshCw className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(audience)}
                    disabled={actionId === audience.id}
                    className="text-destructive hover:text-destructive/80"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  )
}
